import { RoleEnum, isRole } from "./constants/roles";

export const MODULE_ACCESS_LEVEL_ORDER = {
  NONE: 0,
  VIEW: 1,
  EDIT: 2,
  MANAGE: 3,
};

const normalizeAccessLevel = (value, fallback = "NONE") => {
  const normalized = String(value || "").trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(MODULE_ACCESS_LEVEL_ORDER, normalized)
    ? normalized
    : fallback;
};

export const hasLegacyAdminFallback = (user) => isRole(user?.role, RoleEnum.ADMIN);

export const getEffectiveModuleAccess = (user, moduleKey) =>
  normalizeAccessLevel(user?.effective_access?.[moduleKey], "NONE");

export const hasModuleAccess = (
  user,
  moduleKey,
  minimumLevel,
  { allowLegacyAdmin = false } = {}
) => {
  const required = normalizeAccessLevel(minimumLevel, "NONE");
  // Legacy roles do not elevate the configured module level.
  const current = getEffectiveModuleAccess(user, moduleKey);
  return MODULE_ACCESS_LEVEL_ORDER[current] >= MODULE_ACCESS_LEVEL_ORDER[required];
};

export const canAccessAdminUsers = (user, options) =>
  hasModuleAccess(user, "admin_users", "MANAGE", options);

export const canAccessAdminUsersStrict = (user) =>
  hasModuleAccess(user, "admin_users", "MANAGE", { allowLegacyAdmin: false });

export const canAccessAdminModules = (user, options) =>
  hasModuleAccess(user, "admin_modules", "MANAGE", options);
export const canViewAudit = (user, options) =>
  hasModuleAccess(user, "audit", "VIEW", options);

export const canViewIncidents = (user, options) =>
  hasModuleAccess(user, "incidents", "VIEW", options);

export const canEditIncidents = (user, options) =>
  hasModuleAccess(user, "incidents", "EDIT", options);

export const canManageIncidents = (user, options) =>
  hasModuleAccess(user, "incidents", "MANAGE", options);

export const canViewControls = (user, options) =>
  hasModuleAccess(user, "controls", "VIEW", options);

export const canWriteControls = (user, options) =>
  hasModuleAccess(user, "controls", "EDIT", options);

export const canViewActions = (user, options) =>
  hasModuleAccess(user, "actions", "VIEW", options);

export const canViewPolicies = (user, options) =>
  hasModuleAccess(user, "policies", "VIEW", options);

export const canViewGovernance = (user, options) =>
  hasModuleAccess(user, "governance", "VIEW", options);

export const canEditGovernance = (user, options) =>
  hasModuleAccess(user, "governance", "EDIT", options);

export const canManageGovernance = (user, options) =>
  hasModuleAccess(user, "governance", "MANAGE", options);

export const canViewKpis = (user, options) =>
  hasModuleAccess(user, "kpis", "VIEW", options);

export const canEditKpis = (user, options) =>
  hasModuleAccess(user, "kpis", "EDIT", options);

export const canManageKpis = (user, options) =>
  hasModuleAccess(user, "kpis", "MANAGE", options);

export const canViewRisks = (user, options) =>
  hasModuleAccess(user, "risks", "VIEW", options);

export const canViewSupportingEvidence = (user, options) =>
  [canViewRisks, canViewControls, canViewActions, canViewIncidents, canViewAudit, canViewPolicies, canViewGovernance]
    .some((predicate) => predicate(user, options));

// Overview and Task Centre are composed screens, not independent data stores.
export const canViewOverview = (user) => hasModuleAccess(user, "overview", "VIEW");
export const canViewTasks = (user) => hasModuleAccess(user, "tasks", "VIEW") && canViewActions(user);
export const canEditTasks = (user) => hasModuleAccess(user, "tasks", "EDIT") && hasModuleAccess(user, "actions", "EDIT");

export const canAccessWorkspace = (user, workspace) => {
  const modules = {
    risks: "risks", heatmap: "risks", "risk-insights": "risks", "executive-risk-dashboard": "risks",
    controls: "controls", actions: "actions", incidents: "incidents", policies: "policies",
    kpis: "kpis", "executive-kpis": "kpis", audit: "audit", audits: "audit", "audit-plans": "audit", "audit-universe": "audit", boardbacks: "governance",
  };
  if (workspace?.startsWith("admin-") && workspace !== "admin-users") return canAccessAdminModules(user);
  if (workspace === "tasks") return canViewTasks(user);
  if (["my-overview", "company-overview"].includes(workspace)) return canViewOverview(user);
  if (workspace === "supporting-evidence") return canViewSupportingEvidence(user);
  if (["assets", "vendors"].includes(workspace)) {
    return canAccessAdminModules(user) || ["controls", "risks", "audit"].some(key => hasModuleAccess(user, key, "VIEW"));
  }
  if (workspace === "admin-users") return canAccessAdminUsersStrict(user);
  return !modules[workspace] || hasModuleAccess(user, modules[workspace], "VIEW");
};
